
import { useState, useEffect, useRef } from 'react';
import { NoRecords } from '../../../lib/loader';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import moment from 'moment';
import { InternalTransaction } from '../index';
import { Search, Check, Copy, Eye, ExternalLink, CheckIcon } from 'lucide-react';
import { useDebounce } from '../../../hooks/debounce';
import { useParams } from 'react-router-dom';
import { copyToClipboard } from '../../../lib/copytoClipboard';
import { useModalContext } from '../../../contexts/ModalContext';
import { useToast } from '../../../hooks/usetoast';
import { useCheckAdminAccess } from '../../WhiteIp/api/whiteipapi';
import { logout } from '../../../hooks/logout';
import PermissionDenied from '../../common/permissionDenied';
import { WaitingLoader } from '../../../hooks/buttonLoad';
import { useBreadcrumbPagination } from '../../../hooks/useBreadcrumbPagination';
import BreadcrumbPagination from '../../../utils/BreadcrumbPagination';
import { formatAddress } from '../../../utils/formatAddress';
import { Box, Modal, Typography } from '@mui/material';
import { displayValue } from '../../../hooks/export';

function InternalTransactionHistory() {

    const [tempPage, setTempPage] = useState(0);
    const [tempRows, setTempRows] = useState(25);
    const [search, setSearch] = useState("");
    const { theme } = useModalContext();
    const toast = useToast();
    const searchText = useDebounce(search);
    const { id } = useParams();
    const [fromEmail, setFromEmail] = useState(false);
    const [toEmail, setToEmail] = useState(false);

    const { data: getTransactionHistory, isLoading } = InternalTransaction({
        id,
        page: searchText ? 0 : tempPage,
        size: tempRows,
        searchText,
        sortField: "createdAt",
        sortOrder: "desc"
    });

    const totalRecords = getTransactionHistory?.data?.totalRecords || 0;

    const {
        page,
        rowsPerPage,
        totalPages,
        handleChangePage,
        handleChangeRowsPerPage
    } = useBreadcrumbPagination(totalRecords, tempRows);

    useEffect(() => {
        setTempPage(page);
    }, [page]);

    useEffect(() => {
        setTempRows(rowsPerPage);
    }, [rowsPerPage]);

    const inputRef = useRef(null);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
            const length = inputRef.current.value.length;
            inputRef.current.setSelectionRange(length, length);
        }
    });
    useEffect(() => {
        setTempPage(0);
        if (handleChangePage) {
            handleChangePage(null, 0);
        }
    }, [searchText]);



    const copyUserEmailAddress = async data => {
        setFromEmail(data._id)
        await copyToClipboard(data?.senderEmail)
        toast.success("Copied")
        setTimeout(() => {
            setFromEmail()
        }, 800)
    }

    const copyReferralEmailAddress = async data => {
        setToEmail(data._id)
        await copyToClipboard(data?.email)
        toast.success("Copied")
        setTimeout(() => {
            setToEmail()
        }, 800)
    }
    const copyReferralTxnHashAddress = async data => {
        await copyToClipboard(data?.txnHash)
        toast.success("Copied")

    }

    const checkAdminMutation = useCheckAdminAccess();
    const [access, setAccess] = useState(true);

    useEffect(() => {
        checkAdminAccess();
    }, []);
    const [openModal, setOpenModal] = useState(false);
    const [modalData, setModalData] = useState(null);

    const Viewdetails = (row) => {
        setModalData(row);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setModalData(null);
    };

    const copyAdminTxnHashAddress = async (data) => {
        await copyToClipboard(data.admin_trans_txnHash);
        toast.success("Txn Hash copied");
        setTimeout(() => {
        }, 800);
    };



    const checkAdminAccess = async () => {
        try {
            const obj = { page: 'History' };
            const checkAdminRes = await checkAdminMutation.mutateAsync(obj);
            if (!checkAdminRes?.data?.status) {
                setAccess(false);
                logout();
            } else {
                setAccess(true);
            }
        } catch (error) {
            setAccess(false);
        }
    };
    const renderModalTxnCell = (
        hash,
        isVerified,
        row,
        txnUrl,
        onCopy,
        theme
    ) => {
        if (!hash) return "-";

        const txnHashShortened = `${hash.slice(0, 4)}...${hash.slice(-4)}`;

        return (
            <>
                {txnHashShortened}
                {isVerified ? (
                    <CheckIcon size={20} style={{ marginLeft: "5px", color: theme ? "white" : "black" }} />
                ) : (
                    <Copy
                        onClick={() => onCopy(row)}
                        size={20}
                        className="ml-5"
                        style={{
                            cursor: "pointer",
                            marginLeft: "5px",
                            color: theme ? "white" : "black",
                        }}
                    />
                )}
                <a href={`${txnUrl}`} target="_blank" rel="noopener noreferrer" className="ml-2">
                    <ExternalLink style={{ marginLeft: "5px" }} size={20} />
                </a>
            </>
        );
    };

    const renderWithdrawStatus = (withdrawStatus) => {
        const statusClasses = {
            Pending: "text-warning",
            Failed: "text-danger",
            Completed: "text-success", // Optional: explicitly define known success state
        };

        const statusClass = statusClasses[withdrawStatus] || "text-success";

        return <p className={statusClass}>{withdrawStatus}</p>;
    };
    const renderInternalTransactionHistoryTable = () => (
        getTransactionHistory?.data?.transactionHistory?.length > 0 ? (
            <>
                <div style={{ minHeight: "60vh" }}>
                    <div className='table-responsive'>
                        <Table sx={{ minWidth: 650 }} aria-label="internal transaction history table">
                            <TableHead>
                                <TableRow>
                                    <TableCell className='text-nowrap' align="center">S.NO</TableCell>
                                    <TableCell className='text-nowrap' align="center">From Email</TableCell>
                                    <TableCell className='text-nowrap' align="center">To Email</TableCell>
                                    <TableCell className='text-nowrap' align="center">Amount</TableCell>
                                    <TableCell className='text-nowrap' align="center">Txn hash</TableCell>
                                    <TableCell className='text-nowrap' align="center">Status</TableCell>
                                    <TableCell className='text-nowrap' align="center">
                                        Date & Time{' '}
                                    </TableCell>
                                    <TableCell align='center'>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {getTransactionHistory?.data?.transactionHistory?.map((row, i) => (
                                    <TableRow key={row?._id || i}>
                                        <TableCell className='text-nowrap' align="center">{page * rowsPerPage + (i + 1)}</TableCell>
                                        <TableCell align="center">
                                            {row?.senderEmail || "-"}
                                            {fromEmail === row._id ? (
                                                <Check size={20} style={{ marginLeft: "5px", color: theme ? "white" : 'black' }} />
                                            ) : (
                                                <Copy
                                                    onClick={() => copyUserEmailAddress(row)}
                                                    size={20}
                                                    className='ml-5'
                                                    style={{ cursor: "pointer", marginLeft: "5px", color: theme ? "white" : 'black' }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            {row?.email || "-"}
                                            {toEmail === row._id ? (
                                                <Check size={20} style={{ marginLeft: "5px", color: theme ? "white" : 'black' }} />
                                            ) : (
                                                <Copy
                                                    onClick={() => copyReferralEmailAddress(row)}
                                                    size={20}
                                                    className='ml-5'
                                                    style={{ cursor: "pointer", marginLeft: "5px", color: theme ? "white" : 'black' }}
                                                />
                                            )}
                                        </TableCell>

                                        <TableCell className="text-nowrap" align="center">
                                            {row?.amount ? (
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                    {displayValue(row.amount, 6)}
                                                    {row?.currencyImage && (
                                                        <img
                                                            src={row.currencyImage}
                                                            alt="Currency Icon"
                                                            style={{
                                                                height: '20px',
                                                                width: '20px',
                                                                borderRadius: '50%',
                                                                verticalAlign: 'middle',
                                                                objectFit: 'cover',
                                                            }}
                                                        />
                                                    )}
                                                </span>
                                            ) : (
                                                '-'
                                            )}
                                        </TableCell>

                                        <TableCell align="center">
                                            {row?.txnHash ?
                                                <>
                                                    {formatAddress(row?.txnHash) || "-"}

                                                    <Copy
                                                        onClick={() => copyReferralTxnHashAddress(row)}
                                                        size={20}
                                                        className='ml-5'
                                                        style={{ cursor: "pointer", marginLeft: "5px", color: theme ? "white" : 'black' }}
                                                    />
                                                </> : "---"
                                            }

                                        </TableCell>
                                        <TableCell align="center" className=' text-success'>{renderWithdrawStatus(row?.txnStatus)}</TableCell >

                                        <TableCell className='text-nowrap' align="center">
                                            {moment(row?.createdAt).format("DD-MM-YYYY | hh:mm A")}
                                        </TableCell>
                                        <TableCell style={{ whiteSpace: "no-wrap" }} align="center">
                                            <Eye size={20} type='button' onClick={() => Viewdetails(row)}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </div>
                <BreadcrumbPagination
                    page={page}
                    rowsPerPage={rowsPerPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                />





                {/* Modal Popup */}
                <Modal
                    open={openModal}
                    onClose={handleCloseModal}
                    aria-labelledby="withdrawal-modal-title"
                    aria-describedby="withdrawal-modal-description"
                >
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 500,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        borderRadius: 2,
                        p: 4,
                        outline: 'none',
                    }}>
                        {modalData && (
                            <>
                                <Typography id="withdrawal-modal-title" variant="h6" mb={2} fontWeight="bold">
                                    Internal Transfer Details
                                </Typography>


                                {/* User Email */}
                                <Box display="flex" justifyContent="space-between" mb={1}>
                                    <Typography><strong>From Email:</strong></Typography>
                                    <Typography>{modalData?.senderEmail}</Typography>
                                </Box>

                                {/* Address */}
                                <Box display="flex" justifyContent="space-between" mb={1}>
                                    <Typography><strong>To mail:</strong></Typography>
                                    <Typography>{modalData?.email}</Typography>
                                </Box>

                                {/* Status */}
                                <Box display="flex" justifyContent="space-between" mb={1}>
                                    <Typography><strong>Status:</strong></Typography>
                                    <Typography
                                        sx={{
                                            color:
                                                modalData?.txnStatus === 'Completed'
                                                    ? 'green' : 'red'
                                        }}>
                                        {modalData?.txnStatus}
                                    </Typography>
                                </Box>

                                {/* Txn Hash */}
                                <Box display="flex" justifyContent="space-between" mb={1}>
                                    <Typography><strong>Txn Hash:</strong></Typography>
                                    {/* <Typography>

                                        {formatAddress(modalData?.txnHash) || "-"}
                                    </Typography> */}
                                    <Typography>
                                        {renderModalTxnCell(
                                            modalData?.txnHash,
                                            false,
                                            modalData,
                                            modalData?.explorerUrl,
                                            copyAdminTxnHashAddress,
                                            theme
                                        )}
                                    </Typography>
                                </Box>



                                {/* Sent Amount */}
                                <Box display="flex" justifyContent="space-between" mb={1}>
                                    <Typography><strong>Sent Amount:</strong></Typography>
                                    <Typography>
                                        {modalData?.amount}
                                        <img
                                            src={modalData?.currencyImage}
                                            alt={modalData?.currencySymbol}
                                            width="20px"
                                            style={{ marginLeft: '8px' }}
                                        />
                                    </Typography>
                                </Box>


                                {/* Rejection Reason */}
                                {modalData?.txnStatus === 'Failed' && (
                                    <Box display="flex" justifyContent="space-between" mb={1} color="red">
                                        <Typography><strong>Reason:</strong></Typography>
                                        <Typography>{modalData?.reason}</Typography>
                                    </Box>
                                )}

                            </>
                        )}


                    </Box>
                </Modal>
            </>
        ) : (
            <NoRecords />
        )
    );

    let content;

    if (isLoading) {
        content = <WaitingLoader />;
    } else if (access) {
        const tableContent = renderInternalTransactionHistoryTable();

        content = (
            <section>
                <h4>Internal Transaction History</h4>
                <div className="ad_pg_bx bg-white p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="col-md-4">
                            <div className="input-group">
                                <span className="input-group-icon">
                                    <Search size={20} color="#aaa9a9" />
                                </span>
                                <input
                                    type="text"
                                    ref={inputRef}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="form-control"
                                    value={search}
                                    placeholder="Search Email"
                                />
                            </div>
                        </div>
                    </div>
                    {tableContent}
                </div>
            </section>
        );
    } else {
        content = <PermissionDenied />;
    }

    return <>{content}</>;

}

export default InternalTransactionHistory;